import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import nutritionIcon from './assets/nutrition.png';
import backbutton from './assets/backbutton.png';
import arrow from './assets/arrow.webp';
import SEO from './components/SEO';

import './css/RecipeDetails.css';

function RecipeDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [recipe, setRecipe] = useState(null);
  const [contentCreator, setContentCreator] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isNutritionOpen, setIsNutritionOpen] = useState(false);
  const [isInstructionsOpen, setIsInstructionsOpen] = useState(false);
  const [isIngredientsOpen, setIsIngredientsOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [portions, setPortions] = useState(1);
  const [isAiAssistantOpen, setIsAiAssistantOpen] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [audioBlob, setAudioBlob] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [responseAudio, setResponseAudio] = useState(null);
  const [mediaStream, setMediaStream] = useState(null);
  const [textResponse, setTextResponse] = useState(null);
  const [isMetricUnits, setIsMetricUnits] = useState(true); // true for metric (g/kg), false for imperial (oz/lbs)
  const [isMetricVolume, setIsMetricVolume] = useState(true); // true for metric (mL), false for imperial (tsp/tbsp/fl oz/cup)
  
  // Track window width for responsive rendering
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Define mobile breakpoint (adjust as needed)
  const isMobile = windowWidth <= 768;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);
  
  // Function to extract YouTube video ID from URL
  const getYoutubeVideoId = (url) => {
    if (!url) return null;
    
    // Match patterns like: 
    // https://www.youtube.com/watch?v=i84Sc5uvQa8
    // https://youtu.be/i84Sc5uvQa8
    // https://youtube.com/embed/i84Sc5uvQa8
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    
    return (match && match[2].length === 11) ? match[2] : null;
  };
  
  // Function to convert units between metric and imperial
  const convertUnits = (quantity, measurement, toMetric) => {
    if (!measurement || !quantity) return { quantity, measurement };
    
    const lowerMeasurement = measurement.toLowerCase();
    
    // Metric to Imperial conversions
    if (toMetric === false) {
      if (lowerMeasurement.includes('gram') || lowerMeasurement === 'g') {
        return {
          quantity: quantity * 0.035274, // grams to ounces
          measurement: 'oz'
        };
      }
      if (lowerMeasurement.includes('kilogram') || lowerMeasurement === 'kg') {
        return {
          quantity: quantity * 2.20462, // kg to pounds
          measurement: 'lbs'
        };
      }
    }
    
    // Imperial to Metric conversions
    if (toMetric === true) {
      if (lowerMeasurement.includes('ounce') || lowerMeasurement === 'oz') {
        return {
          quantity: quantity * 28.3495, // ounces to grams
          measurement: 'g'
        };
      }
      if (lowerMeasurement.includes('pound') || lowerMeasurement === 'lb' || lowerMeasurement === 'lbs') {
        return {
          quantity: quantity * 453.592, // pounds to grams
          measurement: 'g'
        };
      }
    }
    
    // Return original if no conversion needed
    return { quantity, measurement };
  };

  // Function to convert volume units between metric and imperial
  const convertVolumeUnits = (quantity, measurement, toMetric) => {
    if (!measurement || !quantity) return { quantity, measurement };
    
    const lowerMeasurement = measurement.toLowerCase();
    
    // Imperial to Metric conversions (to mL)
    if (toMetric === true) {
      if (lowerMeasurement.includes('teaspoon') || lowerMeasurement === 'tsp') {
        return {
          quantity: quantity * 5, // tsp to mL
          measurement: 'mL'
        };
      }
      if (lowerMeasurement.includes('tablespoon') || lowerMeasurement === 'tbsp') {
        return {
          quantity: quantity * 15, // tbsp to mL
          measurement: 'mL'
        };
      }
      if (lowerMeasurement.includes('fl oz') || lowerMeasurement === 'fl oz' || (lowerMeasurement.includes('fluid') && lowerMeasurement.includes('ounce'))) {
        return {
          quantity: quantity * 30, // fl oz to mL
          measurement: 'mL'
        };
      }
      if (lowerMeasurement.includes('cup') || lowerMeasurement === 'cup') {
        return {
          quantity: quantity * 250, // cup to mL
          measurement: 'mL'
        };
      }
    }
    
    // Metric to Imperial conversions (from mL)
    if (toMetric === false) {
      if (lowerMeasurement.includes('ml') || lowerMeasurement === 'ml' || lowerMeasurement.includes('milliliter')) {
        // Convert mL to the most appropriate imperial unit
        if (quantity >= 250) {
          return {
            quantity: quantity / 250, // mL to cups
            measurement: 'cup'
          };
        } else if (quantity >= 30) {
          return {
            quantity: quantity / 30, // mL to fl oz
            measurement: 'fl oz'
          };
        } else if (quantity >= 15) {
          return {
            quantity: quantity / 15, // mL to tbsp
            measurement: 'tbsp'
          };
        } else {
          return {
            quantity: quantity / 5, // mL to tsp
            measurement: 'tsp'
          };
        }
      }
    }
    
    // Return original if no conversion needed
    return { quantity, measurement };
  };

  // Function to calculate adjusted ingredient quantity based on portions
  const calculateAdjustedQuantity = (ingredient) => {
    // Handle case where there's no quantity
    if (!ingredient.quantity || ingredient.quantity === null) {
      return ingredient.text;
    }
    
    // Calculate adjusted quantity based on portions
    const originalQuantity = ingredient.quantity;
    const adjustedQuantity = (originalQuantity * portions);
    
    // Check for NaN after initial calculation
    if (isNaN(adjustedQuantity)) {
      return ingredient.text;
    }
    
    // Apply unit conversion if needed
    const converted = convertUnits(adjustedQuantity, ingredient.measurement, isMetricUnits);
    const volumeConverted = convertVolumeUnits(converted.quantity, converted.measurement, isMetricVolume);
    const finalQuantity = volumeConverted.quantity;
    const finalMeasurement = volumeConverted.measurement;
    
    // Check for NaN after conversions
    if (isNaN(finalQuantity)) {
      return ingredient.text;
    }
    
    // Format the adjusted quantity with fractional display
    let formattedQuantity;
    
    // Format fraction display
    if (finalQuantity === 0.125) formattedQuantity = "⅛";
    else if (finalQuantity === 0.25) formattedQuantity = "¼";
    else if (finalQuantity === 0.33 || finalQuantity === 0.333 || (finalQuantity > 0.33 && finalQuantity < 0.34)) formattedQuantity = "⅓";
    else if (finalQuantity === 0.5) formattedQuantity = "½";
    else if (finalQuantity === 0.66 || finalQuantity === 0.666 || (finalQuantity > 0.66 && finalQuantity < 0.67)) formattedQuantity = "⅔";
    else if (finalQuantity === 0.75) formattedQuantity = "¾";
    else if (finalQuantity === 1.25) formattedQuantity = "1¼";
    else if (finalQuantity === 1.33 || (finalQuantity > 1.33 && finalQuantity < 1.34)) formattedQuantity = "1⅓";
    else if (finalQuantity === 1.5) formattedQuantity = "1½";
    else if (finalQuantity === 1.66 || finalQuantity === 1.67) formattedQuantity = "1⅔";
    else if (finalQuantity === 1.75) formattedQuantity = "1¾";
    else if (finalQuantity === 2.25) formattedQuantity = "2¼";
    else if (finalQuantity === 2.33 || (finalQuantity > 2.33 && finalQuantity < 2.34)) formattedQuantity = "2⅓";
    else if (finalQuantity === 2.5) formattedQuantity = "2½";
    else if (finalQuantity === 2.66 || finalQuantity === 2.67) formattedQuantity = "2⅔";
    else if (finalQuantity === 2.75) formattedQuantity = "2¾";
    else if (finalQuantity === 3.25) formattedQuantity = "3¼";
    else if (finalQuantity === 3.33 || (finalQuantity > 3.33 && finalQuantity < 3.34)) formattedQuantity = "3⅓";
    else if (finalQuantity === 3.5) formattedQuantity = "3½";
    else if (finalQuantity === 3.66 || finalQuantity === 3.67) formattedQuantity = "3⅔";
    else if (finalQuantity === 3.75) formattedQuantity = "3¾";
    else if (finalQuantity < 1) {
      formattedQuantity = finalQuantity.toFixed(2).replace(/\.00$/, '').replace(/0+$/, '');
    } else {
      formattedQuantity = finalQuantity % 1 === 0 
        ? finalQuantity.toString() 
        : finalQuantity.toFixed(1).replace(/\.0$/, '');
    }
    
    // Final check for NaN in formatted quantity
    if (formattedQuantity === 'NaN' || isNaN(parseFloat(formattedQuantity))) {
      return ingredient.text;
    }
    
    // Build the formatted ingredient text
    if (finalMeasurement) {
      // Return formatted quantity + measurement + ingredient name
      const ingredientName = ingredient.text.replace(/^[\d\/\.\s]+([\w\s]+)?/, '').trim();
      return (
        <span>
          <span className="ingredient-quantity">{formattedQuantity}</span>
          <span className="ingredient-measurement"> {finalMeasurement}</span>
          {ingredientName ? <span className="ingredient-name"> {ingredientName}</span> : null}
        </span>
      );
    } else {
      // For ingredients with quantity but no measurement
      return (
        <span>
          <span className="ingredient-quantity">{formattedQuantity}</span>
          <span className="ingredient-name"> {ingredient.text.replace(/^\d+(\.\d+)?/, '').trim()}</span>
        </span>
      );
    }
  };

  // Handle back button click
  const handleGoBack = () => {
    navigate(-1); // Navigate back one entry in history stack
  };

  // AI Assistant Functions
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      setMediaStream(stream);
      const recorder = new MediaRecorder(stream);
      const chunks = [];

      recorder.ondataavailable = (e) => {
        chunks.push(e.data);
      };

      recorder.onstop = () => {
        const blob = new Blob(chunks, { type: 'audio/wav' });
        setAudioBlob(blob);
        stream.getTracks().forEach(track => track.stop());
        setMediaStream(null);
        
        // Automatically send to AI after blob is set
        sendToAI(blob);
      };

      setMediaRecorder(recorder);
      recorder.start();
      setIsRecording(true);
    } catch (error) {
      console.error('Error accessing microphone:', error);
      alert('Error accessing microphone. Please ensure you have granted permission.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorder && isRecording) {
      mediaRecorder.stop();
      setIsRecording(false);
      
      // Immediately stop microphone
      if (mediaStream) {
        mediaStream.getTracks().forEach(track => track.stop());
        setMediaStream(null);
      }
    }
  };

  const sendToAI = async (blob = audioBlob) => {
    if (!blob || !recipe) return;

    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append('audio', blob, 'recording.wav');
      formData.append('recipe', JSON.stringify({
        name: recipe.name,
        description: recipe.description,
        ingredients: recipe.ingredients,
        instructions: recipe.instructions,
        calories: recipe.calories,
        proteins: recipe.proteins,
        carbs: recipe.carbs,
        fats: recipe.fats,
        servings: recipe.servings,
        time: recipe.Time
      }));

      const response = await fetch('https://starfish-app-9uavi.ondigitalocean.app/api/voice-chat', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Display text response
      console.log('AI said:', data.textResponse);
      setTextResponse(data.textResponse);

      // Play audio
      const audioBlob = new Blob([
        Uint8Array.from(atob(data.audio), c => c.charCodeAt(0))
      ], { type: 'audio/mpeg' });

      const audioUrl = URL.createObjectURL(audioBlob);
      setResponseAudio(audioUrl);
      
      // Auto play the response
      const audio = new Audio(audioUrl);
      audio.play();
      
    } catch (error) {
      console.error('Error sending to AI:', error);
      alert('Error communicating with AI assistant. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const resetAudioSession = () => {
    setAudioBlob(null);
    setResponseAudio(null);
    setTextResponse(null);
    setIsLoading(false);
  };

  // Function to fetch content creator data
  const fetchContentCreator = async (creatorId) => {
    try {
      const response = await fetch(`https://starfish-app-9uavi.ondigitalocean.app/content-creators/${creatorId}`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Fetched content creator:', data);
      setContentCreator(data);
    } catch (error) {
      console.error('Error fetching content creator:', error);
    }
  };

  // Add useEffect to fetch recipe data
  useEffect(() => {
    const fetchRecipe = async () => {
      try {
        setLoading(true);
        // Parse the ID correctly
        const parsedId = parseInt(id);
        if (isNaN(parsedId)) {
          throw new Error('Invalid recipe ID');
        }
        
        console.log('Fetching recipe with ID:', parsedId);
        
        // Fetch the specific recipe by ID
        const response = await fetch(`https://starfish-app-9uavi.ondigitalocean.app/recipes/${parsedId}`);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        console.log('API response:', data);
        
        // Handle different possible response formats
        let foundRecipe = null;
        
        // Case 1: API returns the recipe object directly
        if (data && data.id === parsedId) {
          foundRecipe = data;
          console.log('Found recipe directly:', foundRecipe);
        } 
        // Case 2: API returns an array of recipes
        else if (Array.isArray(data)) {
          foundRecipe = data.find(r => r.id === parsedId);
          console.log('Found recipe in array:', foundRecipe);
        }
        // Case 3: API returns an object with a recipe property
        else if (data && data.recipe) {
          foundRecipe = data.recipe;
          console.log('Found recipe in object.recipe:', foundRecipe);
        }
        // Case 4: API returns an object with a recipes array
        else if (data && Array.isArray(data.recipes)) {
          foundRecipe = data.recipes.find(r => r.id === parsedId);
          console.log('Found recipe in object.recipes array:', foundRecipe);
        }
        
        if (foundRecipe) {
          setRecipe(foundRecipe);
          // Fetch content creator data if available
          if (foundRecipe.content_creator_id) {
            fetchContentCreator(foundRecipe.content_creator_id);
          }
        } else {
          console.error('Recipe not found in API response');
          setError('Recipe not found');
        }
      } catch (error) {
        console.error('Error fetching recipe:', error);
        setError(`Error fetching recipe: ${error.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchRecipe();
  }, [id]);

  if (loading) {
    return (
      <div className="recipe-details-page">
        <div className="skeleton-container">
          {/* Hero Section Skeleton */}
          <div className="skeleton-hero">
            <div className="skeleton-hero-content">
              <div className="skeleton-creator-section">
                <div className="skeleton-creator-avatar"></div>
                <div className="skeleton-creator-name"></div>
              </div>
              <div className="skeleton-title"></div>
              <div className="skeleton-description"></div>
              <div className="skeleton-meta">
                <div className="skeleton-meta-item"></div>
                <div className="skeleton-meta-item"></div>
                <div className="skeleton-meta-item"></div>
              </div>
            </div>
            <div className="skeleton-hero-image"></div>
          </div>
          
          {/* Content Section Skeleton */}
          <div className="skeleton-content">
            <div className="skeleton-main">
              {/* Ingredients Skeleton */}
              <div className="skeleton-section">
                <div className="skeleton-header">
                  <div className="skeleton-section-title"></div>
                  <div className="skeleton-portion-control"></div>
                </div>
                <div className="skeleton-list">
                  {[...Array(6)].map((_, index) => (
                    <div className="skeleton-list-item" key={index}></div>
                  ))}
                </div>
              </div>
              
              {/* Instructions Skeleton */}
              <div className="skeleton-section">
                <div className="skeleton-section-title"></div>
                <div className="skeleton-instructions">
                  {[...Array(3)].map((_, index) => (
                    <div className="skeleton-instruction-step" key={index}>
                      <div className="skeleton-step-number"></div>
                      <div className="skeleton-step-text"></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            {/* Nutrition Skeleton */}
            <div className="skeleton-nutrition">
              <div className="skeleton-section-title"></div>
              <div className="skeleton-nutrition-grid">
                {[...Array(4)].map((_, index) => (
                  <div className="skeleton-nutrition-item" key={index}></div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return <div className="error-container">
      <h2>Error: {error}</h2>
      <p>There was a problem loading the recipe.</p>
      <Link to="/" className="back-link">Return to recipes</Link>
    </div>;
  }

  if (!recipe) {
    return <div className="error-container">
      <h2>Recipe not found</h2>
      <p>We couldn't find the recipe you're looking for.</p>
      <Link to="/" className="back-link">Return to recipes</Link>
    </div>;
  }
  
  // Check if recipe has a YouTube video URL
  const youtubeVideoId = recipe.youtube_url ? getYoutubeVideoId(recipe.youtube_url) : null;

  // Generate recipe SEO data
  const generateRecipeSEOData = () => {
    if (!recipe) return {};
    
    return {
      title: `${recipe.name} - Recipe | Savory`,
      description: recipe.description || `Learn how to make ${recipe.name}. This delicious recipe includes detailed instructions, nutritional information, and cooking tips.`,
      keywords: `${recipe.name}, recipe, cooking, ${recipe.category || 'food'}, nutrition, calories, proteins`,
      url: `/recipe-details/${recipe.id}`,
      image: recipe.url || '/SavLogo.png',
      type: 'article',
      publishDate: recipe.created_at,
      modifiedDate: recipe.updated_at,
      breadcrumb: [
        { name: "Home", url: "/" },
        { name: "Recipes", url: "/" },
        { name: recipe.name, url: `/recipe-details/${recipe.id}` }
      ],
      recipe: {
        name: recipe.name,
        description: recipe.description,
        url: recipe.url,
        calories: recipe.calories,
        proteins: recipe.proteins,
        fats: recipe.fats,
        carbs: recipe.carbs,
        fiber: recipe.fiber,
        sodium: recipe.sodium,
        ingredients: Array.isArray(recipe.ingredients) ? recipe.ingredients.map(ing => 
          typeof ing === 'string' ? ing : ing.text || ing.name || ''
        ) : [],
        instructions: Array.isArray(recipe.instructions) ? recipe.instructions.map(inst => 
          typeof inst === 'string' ? inst : inst.text || inst.step || ''
        ) : [],
        prepTime: recipe.prep_time,
        cookTime: recipe.cook_time,
        totalTime: recipe.total_time,
        servings: recipe.servings || portions,
        category: recipe.category,
        cuisine: recipe.cuisine,
        creator: contentCreator?.name || 'Savory',
        datePublished: recipe.created_at,
        dateModified: recipe.updated_at,
        videoUrl: recipe.youtube_url,
        rating: recipe.rating,
        reviewCount: recipe.review_count
      }
    };
  };

  const seoData = generateRecipeSEOData();

  return (
    <div className="recipe-details-page">
      {recipe && (
        <SEO 
          title={seoData.title}
          description={seoData.description}
          keywords={seoData.keywords}
          url={seoData.url}
          image={seoData.image}
          type={seoData.type}
          publishDate={seoData.publishDate}
          modifiedDate={seoData.modifiedDate}
          breadcrumb={seoData.breadcrumb}
          recipe={seoData.recipe}
          author={seoData.recipe?.creator}
        />
      )}
      <motion.div 
        className="back-button-container"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <Link to="#" onClick={handleGoBack} className="back-button-details">
          <img src={backbutton} alt="Back" className='backButtonImg'/>
        </Link>
      </motion.div>
      
      <motion.div 
        className="recipe-hero"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
      >
        <div className="hero-content">
        
       
          <motion.h1 
            className="diplomata-regular"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            {recipe.name}
          </motion.h1>
            {/* Content Creator Section */}
            {contentCreator && contentCreator.profile_picture_url && (
              <motion.div 
                className="content-creator-section"
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.1 }}
                onClick={() => navigate(`/content-creator/${contentCreator.id}`)}
                style={{ cursor: 'pointer' }}
              >
                <img 
                  src={contentCreator.profile_picture_url} 
                  alt={contentCreator.name}
                  className="creator-profile-pic"
                />
                <span className="creator-name">by {contentCreator.name}</span>
              </motion.div>
            )}
          <motion.p 
            className="description"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            {recipe.description}
          </motion.p>
          <motion.div 
            className="recipe-meta"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            <div className="meta-item">
              <span className="meta-label">Time</span>
              <span>{recipe.Time || 'N/A'}</span>
            </div>
            <div className="meta-item">
              <span className="meta-label">Servings</span>
              <span>{(recipe.servings || 1) * portions}</span>
            </div>
            <div className="meta-item">
              <span className="meta-label">Calories</span>
              <span>{recipe.calories || 'N/A'} cal</span>
            </div>
            
          </motion.div>
          
        </div>
        <motion.div 
          className="hero-image"
          initial={{ scale: 1.1, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.6 }}
        >
          {youtubeVideoId ? (
            <div className="youtube-video-container">
              <iframe
                src={`https://www.youtube.com/embed/${youtubeVideoId}`}
                title={recipe.name}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="youtube-video"
              ></iframe>
            </div>
          ) : (
            <img 
              src={recipe.url || 'https://via.placeholder.com/400x300?text=No+Image'} 
              alt={recipe.name} 
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = 'https://via.placeholder.com/400x300?text=Image+Error';
              }}
            />
          )}
        </motion.div>
      </motion.div>

      <div className="recipe-content">
        <div className="recipe-main">
          <motion.div 
            className="ingredients-section"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            <div className="ingredients-header">
              <h2 className="ingredients-header-text">Ingredients</h2>
              <div className="ingredients-controls">
                <div className="unit-toggles-container">
                  {/* Weight Unit Toggle Button */}
                  <motion.button
                    className="unit-toggle-button"
                    onClick={() => setIsMetricUnits(!isMetricUnits)}
                    whileTap={{ scale: 0.95 }}
                    transition={{ type: "spring", stiffness: 400, damping: 17 }}
                  >
                    <div className="unit-toggle-track">
                      <motion.div 
                        className="unit-toggle-thumb"
                        animate={{ x: isMetricUnits ? 0 : 40 }}
                        transition={{ type: "spring", stiffness: 300, damping: 20 }}
                      />
                      <span className={`unit-label metric ${isMetricUnits ? 'active' : ''}`}>g/kg</span>
                      <span className={`unit-label imperial ${!isMetricUnits ? 'active' : ''}`}>oz/lb</span>
                    </div>
                  </motion.button>

                  {/* Volume Unit Toggle Button */}
                  <motion.button
                    className="unit-toggle-button"
                    onClick={() => setIsMetricVolume(!isMetricVolume)}
                    whileTap={{ scale: 0.95 }}
                    transition={{ type: "spring", stiffness: 400, damping: 17 }}
                  >
                    <div className="unit-toggle-track">
                      <motion.div 
                        className="unit-toggle-thumb"
                        animate={{ x: isMetricVolume ? 0 : 40 }}
                        transition={{ type: "spring", stiffness: 300, damping: 20 }}
                      />
                      <span className={`unit-label metric ${isMetricVolume ? 'active' : ''}`} style={{marginLeft: '6px'}}>ml</span>
                      <span className={`unit-label imperial ${!isMetricVolume ? 'active' : ''}`} style={{marginRight: '6px'}}>tsp</span>
                    </div>
                  </motion.button>
                </div>

                {/* Portion Controls */}
                <div className="portion-control-container">
                  <div className="portion-controls">
                    <motion.button
                      className="portion-button minus"
                      onClick={() => setPortions(prev => prev <= 1 ? prev / 2 : Math.max(prev - 1, 1))}
                      disabled={portions <= 0.125}
                      whileTap={{ scale: 0.9 }}
                      transition={{ type: "spring", stiffness: 400, damping: 17 }}
                    >
                      <img src={arrow} alt="Decrease" className="arrow-icon" />
                    </motion.button>
                    
                    <motion.div 
                      className="portion-display"
                      key={portions}
                      initial={{ opacity: 0, y: 5 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.15 }}
                    >
                      {portions === 0.125 && <span className="portion-number">⅛</span>}
                      {portions === 0.25 && <span className="portion-number">¼</span>}
                      {portions === 0.5 && <span className="portion-number">½</span>}
                      {portions >= 1 && portions % 1 === 0 && <span className="portion-number">{portions}</span>}
                      {portions >= 1 && portions % 1 === 0.5 && <span className="portion-number">{Math.floor(portions)}½</span>}
                      {portions >= 1 && portions % 1 === 0.25 && <span className="portion-number">{Math.floor(portions)}¼</span>}
                      {portions >= 1 && portions % 1 === 0.75 && <span className="portion-number">{Math.floor(portions)}¾</span>}
                      {portions >= 1 && ![0, 0.25, 0.5, 0.75].includes(portions % 1) && <span className="portion-number">{portions.toFixed(2)}</span>}
                      <span className="portion-label">Portion{portions !== 1 ? 's' : ''}</span>
                    </motion.div>
                    
                    <motion.button
                      className="portion-button plus"
                      onClick={() => setPortions(prev => prev < 1 ? prev * 2 : prev + 1)}
                      whileTap={{ scale: 0.9 }}
                      transition={{ type: "spring", stiffness: 400, damping: 17 }}
                    >
                      <img src={arrow} alt="Increase" className="arrow-icon" />
                    </motion.button>
                  </div>
                  <div className="portion-slider">
                    <div 
                      className="portion-fill" 
                      style={{ width: `${Math.min(portions * 20, 100)}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
            {/* Replace the existing ingredients list with grouped ingredients */}
            {recipe.ingredient_groups ? (
              <div className="ingredients-groups">
                {Object.entries(recipe.ingredient_groups).map(([groupName, groupIngredients], groupIndex) => (
                  <motion.div 
                    key={groupName}
                    className="ingredient-group"
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.6 + groupIndex * 0.1 }}
                  >
                    <h3 className="ingredient-group-header">{groupName}</h3>
                    <ul className="ingredient-group-list">
                      {groupIngredients.map((ingredient, index) => (
                        <motion.li 
                          key={`${groupName}-${index}`}
                          initial={{ x: -20, opacity: 0 }}
                          animate={{ x: 0, opacity: 1 }}
                          transition={{ delay: 0.7 + groupIndex * 0.1 + index * 0.05 }}
                        >
                          {calculateAdjustedQuantity(ingredient)}
                        </motion.li>
                      ))}
                    </ul>
                  </motion.div>
                ))}
              </div>
            ) : (
              // Fallback to original ingredients display if ingredient_groups doesn't exist
              <ul>
                {!!recipe.ingredients && recipe.ingredients.map((ingredient, index) => (
                  <motion.li 
                    key={index}
                    initial={{ x: -20, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: 0.6 + index * 0.1 }}
                  >
                    {calculateAdjustedQuantity(ingredient)}
                  </motion.li>
                ))}
              </ul>
            )}
          </motion.div>

          <motion.div 
            className="instructions-section"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.7 }}
          >
            <h2>Instructions</h2>
            <div className="instructions-container">
              {recipe.instructions && recipe.instructions.map((step, index) => (
                <motion.div 
                  className="instruction-step"
                  key={index}
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.8 + index * 0.1 }}
                >
                  <div className="step-number">{index + 1}</div>
                  <div className="step-text">{step}</div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>

        <motion.div 
          className="nutrition-card"
          initial={{ x: 20, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ delay: 0.6 }}
        >
          <h2>Nutrition Facts</h2>
          <p className="nutrition-subtitle">Values per serving</p>
          <div className="nutrition-grid">
            <div className="nutrition-item">
              <span className="label">Calories</span>
              <span className="value">{recipe.calories || 'N/A'}</span>
            </div>
            <div className="nutrition-item">
              <span className="label">Proteins</span>
              <span className="value">{recipe.proteins || 'N/A'}g</span>
            </div>
            <div className="nutrition-item">
              <span className="label">Carbs</span>
              <span className="value">{recipe.carbs || 'N/A'}g</span>
            </div>
            <div className="nutrition-item">
              <span className="label">Fats</span>
              <span className="value">{recipe.fats || 'N/A'}g</span>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Only render floating buttons on mobile */}
      {isMobile && (
        <>
          <button 
            className="floating-nutrition-btn"
            onClick={() => setIsNutritionOpen(true)}
            aria-label="Show nutrition facts"
          >
            <img 
              src={nutritionIcon} 
              alt="Nutrition" 
              className="nutrition-icon"
            />
          </button>

          <button 
            className="floating-instructions-btn"
            onClick={() => setIsInstructionsOpen(true)}
            aria-label="Show cooking instructions"
          >
            Instructions
          </button>
 
        </>
      )}

      {/* AI Assistant Floating Button */}
      <button 
        className="floating-ai-assistant-btn"
        onClick={() => setIsAiAssistantOpen(true)}
        aria-label="Open AI Assistant"
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 2C13.1046 2 14 2.89543 14 4V8C14 9.10457 13.1046 10 12 10C10.8954 10 10 9.10457 10 8V4C10 2.89543 10.8954 2 12 2Z" fill="white"/>
          <path d="M19 8C19 11.866 15.866 15 12 15C8.13401 15 5 11.866 5 8" stroke="white" strokeWidth="2" strokeLinecap="round"/>
          <path d="M12 15V19" stroke="white" strokeWidth="2" strokeLinecap="round"/>
          <path d="M8 19H16" stroke="white" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      </button>

      {/* AI Assistant Modal */}
      <AnimatePresence>
        {isAiAssistantOpen && (
          <>
            <motion.div 
              className="ai-assistant-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsAiAssistantOpen(false)}
            />
            
            <motion.div 
              className="ai-assistant-chatbox"
              initial={{ x: "100%", opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: "100%", opacity: 0 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
            >
              <div className="ai-chatbox-header">
                <div className="ai-header-content">
                  <div className="ai-avatar">🤖</div>
                  <div className="ai-header-text">
                    <h4>Recipe Assistant</h4>
                    <span className="ai-status">Ready to help</span>
                  </div>
                </div>
                <button 
                  className="close-ai-chatbox"
                  onClick={() => setIsAiAssistantOpen(false)}
                >
                  ×
                </button>
              </div>
              
              <div className="ai-chatbox-content">
                <div className="ai-welcome-message">
                  <p>Hi! Ask me anything about this recipe. I can help with cooking tips, substitutions, and more!</p>
                </div>
                
                <div className="recording-section">
                  {textResponse && (
                    <div className="ai-text-response">
                      <div className="response-header">
                        <span className="response-icon">💬</span>
                        <span>AI Response</span>
                      </div>
                      <p className="response-text">{textResponse}</p>
                    </div>
                  )}

                  {!isRecording && (
                    <button 
                      className="record-btn-compact"
                      onClick={startRecording}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 2C13.1046 2 14 2.89543 14 4V8C14 9.10457 13.1046 10 12 10C10.8954 10 10 9.10457 10 8V4C10 2.89543 10.8954 2 12 2Z" fill="white"/>
                        <path d="M19 8C19 11.866 15.866 15 12 15C8.13401 15 5 11.866 5 8" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                        <path d="M12 15V19" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                        <path d="M8 19H16" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                      Hold to Record
                    </button>
                  )}
                  
                  {isRecording && (
                    <button 
                      className="record-btn-compact recording"
                      onClick={stopRecording}
                    >
                      <div className="recording-pulse"></div>
                      Recording...
                    </button>
                  )}
                  
                  {isLoading && (
                    <div className="loading-ai-compact">
                      <div className="ai-loading-dots">
                        <span></span>
                        <span></span>
                        <span></span>
                      </div>
                      <p>Thinking...</p>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isNutritionOpen && (
          <>
            <motion.div 
              className="mobile-nutrition-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsNutritionOpen(false)}
            />
            
            <motion.div 
              className="mobile-nutrition-panel"
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
            >
              <div className="nutrition-panel-header">
                <h3>Nutrition Facts</h3>
                <button 
                  className="close-nutrition"
                  onClick={() => setIsNutritionOpen(false)}
                >
                  ×
                </button>
              </div>
              <p className="nutrition-subtitle">Values per serving</p>

              <div className="nutrition-facts-grid">
                <div className="nutrition-fact-item">
                  <span className="fact-value">{recipe.calories || 'N/A'}</span>
                  <span className="fact-label">Calories</span>
                </div>
                <div className="nutrition-fact-item">
                  <span className="fact-value">{recipe.proteins || 'N/A'}g</span>
                  <span className="fact-label">Protein</span>
                </div>
                <div className="nutrition-fact-item">
                  <span className="fact-value">{recipe.carbs || 'N/A'}g</span>
                  <span className="fact-label">Carbs</span>
                </div>
                <div className="nutrition-fact-item">
                  <span className="fact-value">{recipe.fats || 'N/A'}g</span>
                  <span className="fact-label">Fat</span>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isInstructionsOpen && (
          <>
            <motion.div 
              className="mobile-instructions-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsInstructionsOpen(false)}
            />
            
            <motion.div 
              className="mobile-instructions-panel"
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
            >
              <div className="instructions-panel-header">
                <h3>How to Cook</h3>
                <button 
                  className="close-instructions"
                  onClick={() => setIsInstructionsOpen(false)}
                >
                  ×
                </button>
              </div>

              <div className="instructions-steps-list">
                {recipe.instructions && recipe.instructions.map((step, index) => (
                  <div key={index} className="instruction-step-item">
                    <div className="step-number">{index + 1}</div>
                    <p className="step-text">{step}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
      
      {/* Mobile Ingredients Panel with Portion Control */}
      <AnimatePresence>
        {isIngredientsOpen && (
          <>
            <motion.div 
              className="mobile-ingredients-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsIngredientsOpen(false)}
            />
            
            <motion.div 
              className="mobile-ingredients-panel"
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
            >
              <div className="ingredients-panel-header">
                <h3 className='ingredients-header'>Ingredients</h3>
                <button 
                  className="close-ingredients"
                  onClick={() => setIsIngredientsOpen(false)}
                >
                  ×
                </button>
              </div>
              
              <div className="mobile-ingredients-controls">
                <div className="mobile-unit-toggles-container">
                  {/* Mobile Weight Unit Toggle */}
                  <motion.button
                    className="unit-toggle-button mobile-unit-toggle"
                    onClick={() => setIsMetricUnits(!isMetricUnits)}
                    whileTap={{ scale: 0.95 }}
                    transition={{ type: "spring", stiffness: 400, damping: 17 }}
                  >
                    <div className="unit-toggle-track">
                      <motion.div 
                        className="unit-toggle-thumb"
                        animate={{ x: isMetricUnits ? 0 : 40 }}
                        transition={{ type: "spring", stiffness: 300, damping: 20 }}
                      />
                      <span className={`unit-label metric ${isMetricUnits ? 'active' : ''}`}>g/kg</span>
                      <span className={`unit-label imperial ${!isMetricUnits ? 'active' : ''}`}>oz/lb</span>
                    </div>
                  </motion.button>

                  {/* Mobile Volume Unit Toggle */}
                  <motion.button
                    className="unit-toggle-button mobile-unit-toggle"
                    onClick={() => setIsMetricVolume(!isMetricVolume)}
                    whileTap={{ scale: 0.95 }}
                    transition={{ type: "spring", stiffness: 400, damping: 17 }}
                  >
                    <div className="unit-toggle-track">
                      <motion.div 
                        className="unit-toggle-thumb"
                        animate={{ x: isMetricVolume ? 0 : 40 }}
                        transition={{ type: "spring", stiffness: 300, damping: 20 }}
                      />
                      <span className={`unit-label metric ${isMetricVolume ? 'active' : ''}`}>mL/L</span>
                      <span className={`unit-label imperial ${!isMetricVolume ? 'active' : ''}`}>cup</span>
                    </div>
                  </motion.button>
                </div>

                {/* Mobile Portion Control */}
                <div className="mobile-portion-control">
                  <motion.button
                    className="portion-button minus"
                    onClick={() => setPortions(prev => prev <= 1 ? prev / 2 : Math.max(prev - 1, 1))}
                    disabled={portions <= 0.125}
                    whileTap={{ scale: 0.92 }}
                    transition={{ type: "spring", stiffness: 400, damping: 17 }}
                  >
                    <img src={arrow} alt="Decrease" className="arrow-icon" />
                  </motion.button>
                  <motion.div 
                    className="portion-display"
                    key={portions}
                    initial={{ opacity: 0, y: 5 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.15 }}
                  >
                    {portions === 0.125 && <span className="portion-number">⅛</span>}
                    {portions === 0.25 && <span className="portion-number">¼</span>}
                    {portions === 0.5 && <span className="portion-number">½</span>}
                    {portions >= 1 && portions % 1 === 0 && <span className="portion-number">{portions}</span>}
                    {portions >= 1 && portions % 1 === 0.5 && <span className="portion-number">{Math.floor(portions)}½</span>}
                    {portions >= 1 && portions % 1 === 0.25 && <span className="portion-number">{Math.floor(portions)}¼</span>}
                    {portions >= 1 && portions % 1 === 0.75 && <span className="portion-number">{Math.floor(portions)}¾</span>}
                    {portions >= 1 && ![0, 0.25, 0.5, 0.75].includes(portions % 1) && <span className="portion-number">{portions.toFixed(2)}</span>}
                    <span className="portion-label">Portion{portions !== 1 ? 's' : ''}</span>
                  </motion.div>
                  <motion.button 
                    className="portion-button plus" 
                    onClick={() => setPortions(prev => prev < 1 ? prev * 2 : prev + 1)}
                    whileTap={{ scale: 0.92 }}
                    transition={{ type: "spring", stiffness: 400, damping: 17 }}
                  >
                    <img src={arrow} alt="Increase" className="arrow-icon" />
                  </motion.button>
                </div>
              </div>

              {/* Mobile grouped ingredients */}
              {recipe.ingredient_groups ? (
                <div className="mobile-ingredients-groups">
                  {Object.entries(recipe.ingredient_groups).map(([groupName, groupIngredients], groupIndex) => (
                    <div 
                      key={groupName}
                      className="mobile-ingredient-group"
                    >
                      <h4 className="mobile-ingredient-group-header">{groupName}</h4>
                      <ul className="mobile-ingredient-group-list">
                        {groupIngredients.map((ingredient, index) => (
                          <li key={`mobile-${groupName}-${index}`}>
                            {calculateAdjustedQuantity(ingredient)}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              ) : (
                // Fallback to original mobile ingredients display
                <ul className="mobile-ingredients-list">
                  {!!recipe.ingredients && recipe.ingredients.map((ingredient, index) => (
                    <li key={index}>
                      {calculateAdjustedQuantity(ingredient)}
                    </li>
                  ))}
                </ul>
              )}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}

export default RecipeDetails;